import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { themes, ColorMode } from '../styles/theme';

type ThemeKey = keyof typeof themes;

interface ThemeContextType {
  activeTheme: ThemeKey;
  mode: ColorMode;
  setTheme: (theme: ThemeKey) => void;
  setMode: (mode: ColorMode) => void;
  availableThemes: ThemeKey[];
}

const ThemeContext = createContext<ThemeContextType>({
  activeTheme: 'green',
  mode: 'dark',
  setTheme: () => undefined,
  setMode: () => undefined,
  availableThemes: Object.keys(themes),
});

export const useAppTheme = () => useContext(ThemeContext);

export const CustomThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeTheme, setActiveThemeState] = useState<ThemeKey>('green');
  const [mode, setModeState] = useState<ColorMode>('dark');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const savedTheme = localStorage.getItem('paira_portfolio_theme') as ThemeKey;
    if (savedTheme && themes[savedTheme]) {
      setActiveThemeState(savedTheme);
    }

    const savedMode = localStorage.getItem('paira_portfolio_mode') as ColorMode;
    if (savedMode === 'light' || savedMode === 'dark') {
      setModeState(savedMode);
    }
  }, []);

  const setTheme = (theme: ThemeKey) => {
    setActiveThemeState(theme);
    localStorage.setItem('paira_portfolio_theme', theme);
  };

  const setMode = (newMode: ColorMode) => {
    setModeState(newMode);
    localStorage.setItem('paira_portfolio_mode', newMode);
  };

  // Prevent hydration mismatch by using default theme until mounted
  const currentTheme = mounted ? themes[activeTheme][mode] : themes.green.dark;

  return (
    <ThemeContext.Provider
      value={{
        activeTheme,
        mode,
        setTheme,
        setMode,
        availableThemes: Object.keys(themes),
      }}
    >
      <StyledThemeProvider theme={currentTheme}>{children}</StyledThemeProvider>
    </ThemeContext.Provider>
  );
};
